import { Button, Form, Modal, Alert, Table } from "react-bootstrap";
import ReactExport from "react-export-excel";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

/**
 * Modal para importar objetivos desde un archivo Excel.
 *
 * Props:
 * - showFormModal: boolean -> controla visibilidad del modal.
 * - setShowFormModal: function -> cierra/abre modal desde el padre.
 * - onImportObjectives: function -> callback con el array de objetivos validados para persistir.
 *
 * Responsabilidades:
 * - Descargar una plantilla de Excel con encabezados esperados.
 * - Validar el tipo/estructura del archivo cargado.
 * - Extraer y previsualizar filas válidas (objetivo, peso, indicador).
 * - Permitir asignar un pilar estratégico por cada objetivo antes de importar.
 *
 */
const ImportObjectivesModal = ({ showFormModal, setShowFormModal, onImportObjectives }) => {
    const ExcelFile = ReactExport.ExcelFile;
    const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
    const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

    // Referencia al input file (no estrictamente necesaria, pero útil si se desea resetear)
    const fileInputRef = useRef();

    // Estado para mostrar errores relacionados al archivo Excel (tipo/estructura/lectura)
    const [excelFileError, setExcelFileError] = useState(null);
    // Estado que contiene la previsualización de objetivos extraídos del Excel
    const [previewData, setPreviewData] = useState([]);


    // Obtener pilares estratégicos desde Redux para permitir asignarlos a cada objetivo
    const {
        loading: loadingPillars,
        strategicPillars,
        strategicPillarError,
    } = useSelector((store) => store.strategicPillar);

    const pillarOptions = strategicPillars?.map((pillar) => ({
        value: pillar.id,
        label: pillar.nombre
    })) || []

    // Datos vacíos para generar plantilla con encabezados en la exportación
    const plantillaData = [{}];

    /**
     * Tipos MIME aceptados para archivos Excel.
     * Incluye formatos legacy y OpenXML (.xls y .xlsx).
     */
    const allowedFileTypes = [
        "application/vnd.ms-excel",
        "application/msexcel",
        "application/x-msexcel",
        "application/x-ms-excel",
        "application/x-excel",
        "application/x-dos_ms_excel",
        "application/xls",
        "application/x-xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    /**
     * handleFileChange
     * - Valida tipo de archivo.
     * - Lee el archivo usando FileReader + xlsx.
     * - Verifica encabezados esperados: ["objetivo", "peso", "indicador"].
     * - Extrae filas válidas y las guarda en previewData.
     */
    const handleFileChange = (e) => {
        const file = e.target.files[0];

        // Validación básica de presencia de archivo
        if (!file) {
            setExcelFileError("Por favor seleccione su archivo.");
            setPreviewData([]);
            return;
        }

        // Validación del tipo MIME; evita procesar archivos no Excel
        if (!allowedFileTypes.includes(file.type)) {
            setExcelFileError("Seleccione solo tipos de archivos de Excel.");
            setPreviewData([]);
            return;
        }

        const reader = new FileReader();
        reader.onload = (evt) => {
            setExcelFileError(null);
            try {
                // Cargar la librería xlsx en tiempo de ejecución
                const XLSX = require("xlsx");
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                // Tomar la primera hoja del libro
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];

                // Convertir hoja a JSON por filas (header: 1 -> devuelve arrays)
                const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                const [headers, ...rows] = json;

                // Validar encabezados mínimos esperados
                if (
                    !headers ||
                    headers.length < 3 ||
                    headers[0] !== "Objetivo" ||
                    headers[1] !== "Peso" ||
                    headers[2] !== "Indicador"
                ) {
                    setExcelFileError("El archivo no tiene los encabezados correctos.");
                    setPreviewData([]);
                    return;
                }

                // Mapear filas a objetos con estructura esperada; ignorar filas incompletas
                const objetivos = rows
                    .filter(row => row.length >= 3)
                    .map(row => ({
                        pilar: '', // pilar se asignará desde la UI antes de importar
                        objetivo: row[0] ?? "",
                        peso: row[1] ?? "",
                        indicador: row[2] ?? ""
                    }));

                if (objetivos.length === 0) {
                    setExcelFileError("No se encontraron objetivos en el archivo.");
                    setPreviewData([]);
                    return;
                }

                setPreviewData(objetivos);
            } catch (err) {
                // Capturar cualquier error en la lectura/parsing y mostrar mensaje 
                setExcelFileError("Error al leer el archivo.");
                setPreviewData([]);
            }
        };
        // Leer como array buffer para compatibilidad con xlsx
        reader.readAsArrayBuffer(file);
    };

    /**
     * handlePilarChange
     * Actualiza el pilar asignado a una fila de previsualización.
     */
    const handlePilarChange = (idx, value) => {
        setPreviewData(prev => prev.map((obj, i) => i === idx ? { ...obj, pilar: value } : obj));
    };

    /**
     * handleImport
     * - Valida que todas las filas tengan un pilar asignado.
     * - Llama al callback onImportObjectives con los datos listos para persistir.
     * - Resetea estados y cierra modal.
     */
    const handleImport = () => {
        // Validación: cada objetivo debe tener un pilar seleccionado
        if (previewData.some(obj => !obj.pilar)) {
            setExcelFileError('Debe seleccionar un pilar estratégico para cada objetivo.');
            return;
        }

        if (previewData.length > 0) {
            onImportObjectives(previewData);
            setShowFormModal(false);
            setPreviewData([]);
            setExcelFileError(null);
        }
    };

    return (
        <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="ft-modal-title">
                    Importar Objetivos
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* 1) Descargar Plantilla: permite al usuario obtener un archivo con los encabezados esperados */}
                <h5 className="my-2">1. Descargar Plantilla</h5>
                <p className="my-2">
                    Haga clic en el botón para descargar la plantilla y rellene las columnas según los encabezados ya establecidos. Es importante que no cambie el nombre de los encabezados.
                </p>
                <ExcelFile
                    element={
                        <Button className="my-3" variant="primary">
                            Descargar Plantilla
                        </Button>
                    }
                    filename="Plantilla_Objetivos"
                >
                    <ExcelSheet data={plantillaData} name="Plantilla">
                        <ExcelColumn label="Objetivo" value={() => ""} />
                        <ExcelColumn label="Peso" value={() => ""} />
                        <ExcelColumn label="Indicador" value={() => ""} />
                    </ExcelSheet>
                </ExcelFile>

                {/* 2) Cargar Archivo: input controlado que valida y previsualiza contenido */}
                <h5 className="my-2">2. Cargar Archivo</h5>
                <p className="my-2">Seleccione el archivo que contiene los objetivos a importar.</p>
                <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label>Seleccionar archivo</Form.Label>
                    <Form.Control
                        type="file"
                        accept=".xlsx,.xls"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                    />
                </Form.Group>

                {/* Mostrar errores de carga/validación */}
                {excelFileError && (
                    <Alert variant="danger" className="mt-2">
                        {excelFileError}
                    </Alert>
                )}

                {/* Previsualización: tabla editable en la columna "Pilar estratégico" */}
                {previewData.length > 0 && (
                    <>
                        <Alert variant="info" className="mt-2">
                            Previsualización de objetivos importados:
                        </Alert>
                        <Table bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>Pilar estratégico</th>
                                    <th>Objetivo</th>
                                    <th>Peso</th>
                                    <th>Indicador</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previewData.map((obj, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            {/* Selección del pilar para cada objetivo (requerido antes de importar) */}
                                            <Form.Control
                                                as="select"
                                                value={obj.pilar}
                                                onChange={e => handlePilarChange(idx, e.target.value)}
                                            >
                                                <option value="">Seleccione...</option>
                                                {pillarOptions.map(p => (
                                                    <option key={p.value} value={p.value}>{p.label}</option>
                                                ))}
                                            </Form.Control>
                                        </td>
                                        <td>{obj.objetivo}</td>
                                        <td>{obj.peso}</td>
                                        <td>{obj.indicador}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                {/* Cancelar: cierra modal y mantiene estado actual en caso de reabrir */}
                <Button variant="secondary" className="rounded-pill" onClick={() => setShowFormModal(false)}>
                    Cancelar
                </Button>

                {/* Importar: ejecuta la validación final y envía datos al contenedor */}
                <Button
                    variant="primary"
                    className="rounded-pill"
                    onClick={handleImport}
                    disabled={previewData.length === 0}
                >
                    Importar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};


export default ImportObjectivesModal;